import { createSelector } from '@reduxjs/toolkit';
import { type StaffPermission } from '@icebreaker/shared/staff';
import { SCREEN_PERMISSIONS } from '@/app/screen-permissions';
import { SCREEN_ROUTES } from '@/app/routes';
import { type RootState } from '@/shared/store/store';

export const selectAuth = (state: RootState) => state.auth;
export const selectStaff = (state: RootState) => state.auth.staff;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectChallenge = (state: RootState) => state.auth.challenge;
export const selectLoginRequestState = (state: RootState) => state.auth.requestState;
export const selectVerifyState = (state: RootState) => state.auth.verifyState;
export const selectSetupState = (state: RootState) => state.auth.setupState;

/**
 * EKRAN GÖRÜNÜRLÜĞÜ — yetki `staff_accounts.role`ten türeyen sunucu kararıdır
 * ve istemci onu YENİDEN HESAPLAMAZ: `GET /panel/auth/me` yanıtındaki listeyi
 * okur. Rol → yetki tablosunu istemcide bir kez daha yazmak, iki tarafın
 * ayrışabildiği bir kopya üretirdi.
 *
 * BU BİR GÜVENLİK SINIRI DEĞİLDİR. Gerçek sınır `StaffPermissionsGuard`tır;
 * burada verilen karar yalnız "bu bağlantıyı çiz / çizme"dir.
 */
export const selectHasStaffPermission =
  (permission: StaffPermission) =>
  (state: RootState): boolean =>
    holds(state.auth.staff?.permissions, permission);

/**
 * KARARIN KENDİSİ — TEK FONKSİYON.
 *
 * Hem tekil seçici hem gezinme listesi buradan geçer; iki yerde `includes`
 * yazmak, birinin bir gün "admin ise her şey" gibi bir kısayol öğrenmesine ve
 * ikisinin sessizce ayrışmasına açık kapı bırakırdı.
 */
function holds(
  permissions: readonly StaffPermission[] | undefined,
  permission: StaffPermission | undefined,
): boolean {
  return permission !== undefined && (permissions?.includes(permission) ?? false);
}

/** Oturum yokken de KARARLI referans: boş dizi her seferinde yeniden doğmaz. */
const NO_PERMISSIONS: readonly StaffPermission[] = [];

const selectStaffPermissions = (state: RootState): readonly StaffPermission[] =>
  state.auth.staff?.permissions ?? NO_PERMISSIONS;

/**
 * YAN GEZİNMEDE ÇİZİLECEK EKRANLAR — kabuğun TEK kaynağı (QA S2/S3 · P2-1).
 *
 * Yetkisi olmayan personel kullanamayacağı bağlantıyı GÖRMEZ. Karar
 * `SCREEN_PERMISSIONS` envanterinden ve `holds`tan gelir; envanterde
 * KARŞILIĞI OLMAYAN rota da çizilmez (fail-closed) — bir ekran eklenip
 * sınıflandırılmazsa `screen-permission-coverage.guard.spec` zaten kırılır.
 *
 * `createSelector` referans kararlılığı içindir: her render'da yeni dizi
 * döndürmek `useSelector`da sonsuz yeniden çizim üretir.
 */
export const selectVisibleScreenRoutes = createSelector(
  [selectStaffPermissions],
  (permissions): readonly string[] =>
    SCREEN_ROUTES.filter((route) => holds(permissions, SCREEN_PERMISSIONS[route]?.permission)),
);
